<template>
  <div class="AccessTree relative grid grid-cols-6 col-span-6 h-12 hover:bg-gray-100 items-center">
    <div 
      class="flex col-span-3 h-12 items-center"
      :class="{'ml-3': depth < 1}"
    >
      <template v-if="depth > 0">
        <div class="AccessTree__PreBranchWrapper h-full flex flex-row">
          <div
            v-for="(branch, i) in numOfPreBranches"
            :key="i"
            class="AccessTree__PreBranch relative h-full w-1 border-gray-400 border-solid border-l-2 pointer-events-none"
            :class="[
              'AccessTree__PreBranch-level-' + i, 
              {'border-none': preBranchesToHide.includes(i)}
            ]"
          />
        </div>
        <div 
          class="AccessTree__Branch relative h-full w-8 mr-1 rounded-bl-lg border-gray-400 border-solid border-b-2 border-l-2 pointer-events-none z-9" 
          :class="{'AccessTree__Branch-only': !numOfPreBranches}"
        />
        <aw-icon
          v-if="node.children.length"
          :icon="toggleChildren ? 'minus' : 'plus'"
          class="AccessTree__BranchToggle relative bg-white mr-1 h-5 w-5 text-gray-400 hover:text-gray-500 cursor-pointer z-10"
          @click="toggleChildren = !toggleChildren"
        />
        <div
          v-else
          class="AccessTree__BranchTogglePlaceholder relative bg-white mr-1 z-10"
        />
      </template>
      <template v-if="!parentSet">
        <input
          type="text"
          :name="'access[' + node.id + ']'"
          :value="right"
          class="hidden"
        />
        <input
          type="text"
          :name="'meta[' + node.id + ']'"
          :value="metaComputed"
          class="hidden"
        />
      </template>
      <div
        class="AccessTree__Label overflow-ellipsis overflow-hidden text-sm"
        :class="{ 'font-medium': depth === 0 }"
      >
        <span
          class="whitespace-nowrap"
        >
          {{ node.label }}
        </span>
      </div>
    </div>
    <template v-if="isFirst">
      <div class="col-span-1 text-center whitespace-nowrap w-full">
        <div class="inline-flex items-center">
          <tippy-guide-item-wrapper
            v-if="node.maxAccess >= 1"
            :content-message="'Content will be hidden for all team users'"
            :item-index="0"
            :group-id="'user-rights-guide'"
          >
            <span
              @click="setAccess('hide')"
              class="cursor-pointer text-gray-400"
              :class="{ 'text-bc-blue': right == 'hide' }"
            >
              <aw-icon
                icon="eye-closed"
                class="w-4 h-4 "
              />
            </span>
          </tippy-guide-item-wrapper>
        </div>
      </div>
      <div class="text-center whitespace-nowrap w-full">
        <div class="inline-flex items-center space-x-4">
          <tippy-guide-item-wrapper
            v-if="node.maxAccess >= 2"
            :content-message="'Content will be visible for all team users'"
            :item-index="1"
            :group-id="'user-rights-guide'"
          >
            <span
              @click="setAccess('')"
              class="cursor-pointer text-gray-400"
              :class="{ 'text-green-600': right == '' }"
            >
              <aw-icon
                icon="eye-opened"
                class="w-4 h-4"
              />
            </span>
          </tippy-guide-item-wrapper>
          <tippy-guide-item-wrapper
            v-if="node.maxAccess >= 3"
            :content-message="'Disable share and download options for all team users'"
            :item-index="2"
            :group-id="'user-rights-guide'"
          >
            <aw-icon
              @click="toggledisable_download()"
              icon="download"
              class="
                text-gray-400
                w-4
                h-4
                cursor-pointer
              "
              :class="{ 'text-green-600': !disable_download && right == '' }"
            />
          </tippy-guide-item-wrapper>
          <span
            v-else
            class="w-4 h-4 inline-block"
          />
        </div>
      </div>
      <div class="text-center whitespace-nowrap w-full">
        <div class="inline-flex items-center space-x-4">
          <tippy-guide-item-wrapper
            v-if="node.maxAccess >= 4"
            :content-message="'Enable editing rights for all team users'"
            :item-index="3"
            :group-id="'user-rights-guide'"
          >
            <span
              @click="setAccess('edit')"
              class="cursor-pointer text-gray-400"
              :class="{ 'text-red-600': right == 'edit' }"
            >
              <aw-icon icon="pen" class="w-4 h-4" />
            </span>
          </tippy-guide-item-wrapper>
          <tippy-guide-item-wrapper
            v-if="node.maxAccess == 5"
            :content-message="'Lock for all team users'"
            :item-index="4"
            :group-id="'user-rights-guide'"
          >
            <aw-icon
              @click="toggleLock()"
              icon="lock"
              class="
                text-gray-400
                w-4
                h-4
                cursor-pointer
              "
              :class="{ 'text-red-600': lock }"
            />
          </tippy-guide-item-wrapper>
          <span
            v-else
            class="w-4 h-4 inline-block"></span>
        </div>
      </div>
    </template>
    <template v-else>
      <div class="col-span-1 text-center whitespace-nowrap w-full">
        <div class="inline-flex items-center">
          <span
            v-if="node.maxAccess >= 1"
            @click="setAccess('hide')"
            class="cursor-pointer text-gray-400"
            :class="{ 'text-bc-blue': right == 'hide' }"
          >
            <aw-icon
              icon="eye-closed"
              class="w-4 h-4 "
            />
          </span>
        </div>
      </div>
      <div class="text-center whitespace-nowrap w-full">
        <div class="inline-flex items-center space-x-4">
          <span
            v-if="node.maxAccess >= 2"
            @click="setAccess('')"
            class="cursor-pointer text-gray-400"
            :class="{ 'text-green-600': !parentSet && right == '' }"
          >
            <aw-icon
              icon="eye-opened"
              class="w-4 h-4"
            />
          </span>
          <aw-icon
            v-if="node.maxAccess >= 3"
            @click="toggledisable_download()"
            icon="download"
            class="
              text-gray-400
              w-4
              h-4
              cursor-pointer
            "
            :class="{ 'text-green-600': !parentSet && !disable_download && right == '' }"
          />
          <span
            v-else
            class="w-4 h-4 inline-block"
          />
        </div>
      </div>
      <div class="text-center whitespace-nowrap w-full">
        <div class="inline-flex items-center space-x-4">
          <span
            v-if="node.maxAccess >= 4"
            @click="setAccess('edit')"
            class="cursor-pointer text-gray-400"
            :class="{ 'text-red-600': right == 'edit' }"
          >
            <aw-icon icon="pen" class="w-4 h-4" />
          </span>
          <aw-icon
            v-if="node.maxAccess == 5"
            @click="toggleLock()"
            icon="lock"
            class="
              text-gray-400
              w-4
              h-4
              cursor-pointer
            "
            :class="{ 'text-red-600': lock }"
          />
          <span
            v-else
            class="w-4 h-4 inline-block"
          />
        </div>
      </div>
    </template>
  </div>
  <div
    v-for="(nodeChild, i) in node.children"
    :key="nodeChild.id"
    v-show="toggleChildren || depth < 1"
    class="AccessTree__Wrapper grid grid-cols-6 col-span-6"
  >
    <access-tree
      :node="nodeChild"
      :depth="depth + 1"
      :pre-branches-to-hide="extendPreBranchesToHide(i)"
      :parent-meta="meta"
      :parent-right="right"
      :updated-child-right="updateChildRight"
      :updated-child-lock="updateChildLock"
      @rightUpdated="rightUpdated"
      @lockUpdated="lockUpdated"
    />
  </div>
</template>

<script>
export default {
  emits: ['rightUpdated', 'lockUpdated'],
  props: ['node', 'depth', 'isFirst', 'preBranchesToHide', 'parentMeta', 'parentRight', 'updatedChildRight', 'updatedChildLock'],
  data() {
    return {
      right: this.parentRight ? this.parentRight : this.node.right,
      meta: this.node.meta,
      changed: false,
      toggleChildren: false,
      updateChildRight: false,
      updateChildLock: false,
    }
  },
  methods: {
    extendPreBranchesToHide(i) {
      if (i+1 === this.node.children.length) {
        return [...this.preBranchesToHide, this.depth]
      }
      return this.preBranchesToHide
    },
    rightUpdated(childRight) {
      if (this.right == childRight) {
        return
      }
      this.updateChildRight = false
      this.right = ''
      this.$emit('rightUpdated', this.right)
      /*
      TDOD: delete or fix -> childLock is undefined variable
      if (this.right === 'edit') {
        this.meta = childLock
        this.$emit('lockUpdated', childLock)
      }
      */
    },
    lockUpdated(childLock) {
      if (this.right === 'edit') {
        this.meta = childLock
        this.updateChildLock = false
        this.$emit('lockUpdated', childLock)
      }
    },
    setAccess(state) {
      if (state !== this.right) {

        this.changed = true
        this.right = state
        if (state != 'edit') {
          this.setLock(false)
        }

        if (state != '') {
          this.setDisableDownload(false)
        }
      }

      this.updateChildRight = true
      this.$emit('rightUpdated', this.right)
    },
    setDisableDownload(disable_download) {
      this.meta = disable_download ? 'disable_download' : null
    },
    toggledisable_download() {
      if (this.right != '') {
        this.setAccess('')
      } else {
        this.setDisableDownload(!this.disable_download)
        this.$emit('rightUpdated', '')
      }
    },
    setLock(lock) {
      this.meta = lock ? 'lock' : null
      this.updateChildLock = true
      this.$emit('lockUpdated', this.meta)
    },
    toggleLock() {
      if (this.right != 'edit') {
        this.setAccess('edit')
      } else {
        this.$emit('rightUpdated', 'edit')
      }
      this.setLock(!this.lock)
    },
    childrenHaveSameRight(children) {
      return children.every(
        item => {
          return item.right === this.node.right
            && (item.children.length === 0 || this.childrenHaveSameRight(item.children))  
        })
    }
  },
  computed: {
    disable_download() {
      return this.meta === 'disable_download' && this.right === ''
    },
    lock() {
      return this.meta === 'lock' && this.right === 'edit'
    },
    metaComputed() {
      return this.node.maxAccess === 2 && this.right === '' ? 'disable_download' : this.meta
    },
    numOfPreBranches() {
      return this.depth - 1
    },
    parentSet() {
      return this.parentRight == 'edit' || this.parentRight == 'hide'
    },
  },
  created() {
    this.toggleChildren = !this.childrenHaveSameRight(this.node.children)
  },
  watch: {
    parentMeta(newVal, oldVal) {
      if (newVal === 'disable_download') {
        this.meta = 'disable_download'
      } else if (newVal === null && oldVal === 'disable_download') {
        this.meta = null
      }
      if ((newVal === 'lock' || (newVal === null && oldVal === 'lock')) && this.updatedChildLock) {
        this.meta = newVal
        this.updateChildLock = true
      }
    },
    parentRight(newVal) {
      if (!this.updatedChildRight) {
        return
      }
      this.right = newVal
      this.updateChildRight = true
    }
  }
}
</script>

<style lang="scss" scoped>
.AccessTree__Wrapper {
  margin-top: -6px;
}

.AccessTree:hover {
  @apply bg-gray-100;

  .AccessTree__BranchToggle,
  .AccessTree__BranchTogglePlaceholder,
  svg {
    @apply bg-gray-100;
  }
}

.AccessTree:nth-child(2) {
  @apply bg-white;
  @apply z-10;
}

.AccessTree:nth-child(2):hover {
  @apply bg-gray-100;
}

.AccessTree__PreBranch,
.AccessTree__Branch {
  transform: translate3D(0, -25px, 0);
  margin-left: 19px;
  height: calc(100% + 2px);
}

.AccessTree__PreBranch-level-0,
.AccessTree__Branch-only {
  margin-left: 1rem;
}

.AccessTree__BranchToggle {
  margin-left: -22px;
  min-width: 1.25rem;
}

.AccessTree__BranchTogglePlaceholder {
  margin-left: -4px;
  width: 4px;
  height: 4px;
}

.dot {
  @apply w-2.5 h-2.5 border border-gray-600 rounded-full;
}

.dot--blue {
  @apply bg-bc-blue border-bc-blue;
}

.dot--green {
  @apply bg-green-600 border-green-600;
}

.dot--red {
  @apply bg-red-600 border-red-600;
}
</style>
